import dayjs from 'dayjs'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { ReactElement, useCallback, useMemo, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { Add, Edit, Delete, Visibility } from '@mui/icons-material'
import {
  fetchAssignmentSummariesV4,
  selectAssignmentSummariesV4,
  selectAssignmentSummariesV4Status,
  selectLinkedServices,
  selectLinkedServicesStatus,
  activeLinkedServiceChanged,
  activeAssignmentV4Changed,
  type AssignmentSummaryV3,
  fetchLinkedServices,
} from 'src/v3/manage/manage.slice'
import { AuditLogIcon, SearchIcon } from 'src/v3/core/icons'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import AssignmentMetrics from './components/assignment-metrics'
import AuditLogModal from '../components/audit-log-modal'
import AssignmentStatusIndicator from './components/assignment-status-indicator'
import useAssignmentFilters from './hooks/use-assignment-filters'
import { useLinkedServiceSelection } from '../hooks/use-linked-service-selection'
import {
  ASSIGNMENTS_URLS,
  MIN_BUTTON_SIZE,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../constants'
import { deleteAssignment } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import { getIsOverdue, getProgressColor } from './utils/helpers'
import { dataGridSx } from '../styles'

export default function AssignmentsV4(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let location = useLocation()
  let linkedServiceID = useLinkedServiceSelection()
  let { filters, actions, filterAssignments } = useAssignmentFilters()
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [assignmentToDelete, setAssignmentToDelete] =
    useState<AssignmentSummaryV3 | null>(null)
  let [isDeleting, setIsDeleting] = useState<boolean>(false)
  let [auditLogOpen, setAuditLogOpen] = useState<boolean>(false)

  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let assignments = useAppSelector(selectAssignmentSummariesV4)
  let assignmentSummariesStatus = useAppSelector(
    selectAssignmentSummariesV4Status,
  )
  let linkedServices = useAppSelector(selectLinkedServices)

  let { nameFilter, statusFilter, dateLowerFilter, dateUpperFilter } = filters
  let {
    setNameFilter,
    setStatusFilter,
    setDateLowerFilter,
    setDateUpperFilter,
    resetFilters,
  } = actions
  let assignmentsByService =
    linkedServiceID && linkedServiceID !== 'unselected'
      ? assignments.filter((a) => a.linkedServiceID === linkedServiceID)
      : assignments
  let filteredAssignments = filterAssignments(assignmentsByService)
  let isFiltered = nameFilter !== '' || statusFilter !== 'All'
  let isLoaded =
    assignmentSummariesStatus === 'fulfilled' ||
    assignmentSummariesStatus === 'rejected'
  let isLoading = assignmentSummariesStatus === 'pending'
  let totalAssignments = filteredAssignments.length
  let completedAssignments = filteredAssignments.filter(
    (a) => a.completionPercentage === 100,
  ).length
  let overdueAssignments = filteredAssignments.filter(
    (a) =>
      a.dueDate &&
      dayjs(a.dueDate).isBefore(dayjs(), 'day') &&
      a.completionPercentage < 100,
  ).length
  let overallCompletionRate =
    totalAssignments > 0
      ? Math.round(
          filteredAssignments.reduce(
            (sum, a) => sum + a.completionPercentage,
            0,
          ) / totalAssignments,
        )
      : 0

  let handleAssignmentClick = (assignment: AssignmentSummaryV3) => {
    dispatch(activeAssignmentV4Changed(assignment.id))
    navigate(`${ASSIGNMENTS_URLS.INDEX}/${assignment.id}`)
  }

  let handleDeleteAssignment = async () => {
    if (!assignmentToDelete) return

    setIsDeleting(true)

    await deleteAssignment(assignmentToDelete.id)
      .then(() => {
        dispatch(fetchAssignmentSummariesV4())
        showSuccess('Assignment deleted successfully.')
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(
          'Error deleting assignment:',
          assignmentToDelete.id,
          error,
        )
        showError('Error deleting assignment.')
      })
      .finally(() => {
        setAssignmentToDelete(null)
        setIsDeleting(false)
      })
  }

  useAutoFetch(assignmentSummariesStatus, fetchAssignmentSummariesV4)
  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (location.state?.successMessage) {
      showSuccess(location.state.successMessage)
      // Clear the message so it doesn't show again on navigation
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, navigate, showSuccess])

  let gridColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box className="flex items-center" sx={{ height: '100%' }}>
            <AssignmentStatusIndicator
              completionPercentage={params.row.completionPercentage}
              dueDate={params.row.dueDate}
            />
          </Box>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'createdTime',
        headerName: 'Created Date',
        width: 150,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).format('MMM D, YYYY') : '-',
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        width: 150,
        valueFormatter: (value: string | null) =>
          value ? dayjs(value).format('MMM D, YYYY') : '-',
      },
      {
        field: 'courseCount',
        headerName: 'Courses',
        width: 100,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'completedUsers',
        headerName: 'Completed Users',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_value, row) =>
          `${row.completedUserCount}/${row.assignedUserCount}`,
      },
      {
        field: 'completionPercentage',
        headerName: 'Progress',
        width: 150,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value: number) => `${Math.round(value)}%`,
        renderCell: (params) => {
          let assignmentIsOverdue =
            getIsOverdue(params.row.dueDate || '') &&
            dayjs(params.row.dueDate).isBefore(dayjs(), 'day') &&
            params.row.completionPercentage < 100

          return (
            <Box
              className="flex flex-col items-center justify-center"
              sx={{ height: '100%', width: '100%' }}
            >
              <Box
                component="span"
                sx={{
                  mb: 0.5,
                  color: getProgressColor(
                    params.row.completionPercentage,
                    assignmentIsOverdue,
                  ),
                }}
              >
                {Math.round(params.row.completionPercentage)}%
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.round(params.row.completionPercentage)}
                sx={{
                  width: '100%',
                  height: 6,
                  borderRadius: 3,
                }}
                color={getProgressColor(
                  params.row.completionPercentage,
                  assignmentIsOverdue,
                )}
              />
            </Box>
          )
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        filterable: false,
        disableExport: true,
        renderCell: (params) => (
          <Box
            className="flex items-center justify-end"
            sx={{ height: '100%' }}
          >
            <Tooltip title="View">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  handleAssignmentClick(params.row)
                }}
                aria-label={`View ${params.row.name}`}
                sx={{
                  minWidth: MIN_BUTTON_SIZE,
                  minHeight: MIN_BUTTON_SIZE,
                }}
              >
                <Visibility color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`${ASSIGNMENTS_URLS.INDEX}/${params.row.id}/edit`)
                }}
                aria-label={`Edit ${params.row.name}`}
                sx={{
                  minWidth: MIN_BUTTON_SIZE,
                  minHeight: MIN_BUTTON_SIZE,
                }}
              >
                <Edit color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation()
                  setAssignmentToDelete(params.row)
                }}
                aria-label={`Delete ${params.row.name}`}
                color="error"
                sx={{
                  minWidth: MIN_BUTTON_SIZE,
                  minHeight: MIN_BUTTON_SIZE,
                }}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [navigate, handleAssignmentClick],
  )

  let gridRows = useMemo(
    () =>
      filteredAssignments.map((a) => ({
        id: a.id,
        name: a.name,
        linkedServiceID: a.linkedServiceID,
        createdTime: a.createdTime,
        dueDate: a.dueDate,
        courseCount: a.courseCount,
        assignedUserCount: a.assignedUserCount,
        completedUserCount: a.completedUserCount,
        completionPercentage: a.completionPercentage,
        lastActivityDate: a.lastActivityDate,
      })),
    [filteredAssignments],
  )

  let AssignmentsToolbar = useCallback(
    () => (
      <Box sx={{ backgroundColor: 'background.paperDark' }}>
        <DataGridToolbar fileName="assignments" />
      </Box>
    ),
    [],
  )

  return (
    <Container maxWidth="xl" className="py-6">
      <Typography variant="h4" component="h1" className="mb-6">
        Assignments
      </Typography>
      {isLoaded ? (
        <AssignmentMetrics
          totalAssignments={totalAssignments}
          overallCompletionRate={overallCompletionRate}
          completedAssignments={completedAssignments}
          overdueAssignments={overdueAssignments}
        />
      ) : (
        <Box
          className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2 md:grid-cols-4"
          role="status"
          aria-busy="true"
          aria-label="Loading assignment metrics"
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Card
              key={index}
              className="flex flex-col justify-center h-full p-6 text-center"
              sx={{ bgcolor: 'background.paper' }}
              aria-hidden="true"
            >
              <Box className="flex items-center justify-center mb-2">
                <Skeleton
                  variant="rectangular"
                  width={32}
                  height={32}
                  className="mr-4"
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton variant="text" width={60} height={40} />
              </Box>
              <Skeleton
                variant="text"
                width="70%"
                height={20}
                className="mx-auto"
              />
              {index === 1 && (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={8}
                  className="mt-4"
                  sx={{ borderRadius: 4 }}
                />
              )}
            </Card>
          ))}
        </Box>
      )}
      {isLoaded ? (
        <Card className="p-6 mb-6" sx={{ bgcolor: 'background.paper' }}>
          <Box className="grid grid-cols-1 gap-6 lg:grid-rows-2 lg:grid-cols-3">
            <FormControl
              sx={{ minWidth: 180, backgroundColor: 'background.paper' }}
            >
              <InputLabel id="linked-service-filter">Linked Service</InputLabel>
              <Select
                labelId="linked-service-filter"
                label="Linked Service"
                value={linkedServiceID || ''}
                onChange={(event) =>
                  dispatch(activeLinkedServiceChanged(event.target.value))
                }
                size="medium"
                displayEmpty
              >
                {linkedServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Search assignments"
              placeholder="Search by name"
              size="medium"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              sx={{ minWidth: 200, backgroundColor: 'background.paper' }}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon className="mr-2" />,
                },
              }}
            />

            <FormControl
              sx={{ minWidth: 180, backgroundColor: 'background.paper' }}
            >
              <InputLabel id="status-filter">Status</InputLabel>
              <Select
                labelId="status-filter"
                label="Status"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value as
                      'All' | 'Active' | 'Overdue' | 'Completed',
                  )
                }
                size="medium"
                displayEmpty
              >
                <MenuItem value="All">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Overdue">Overdue</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </FormControl>

            <Box className="col-span-1 lg:col-span-3">
              <Box className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From"
                    value={dayjs(dateLowerFilter)}
                    onChange={setDateLowerFilter}
                    slotProps={{
                      textField: {
                        size: 'medium',
                        sx: { bgcolor: 'background.paper' },
                      },
                    }}
                  />
                  <DatePicker
                    label="To"
                    value={dayjs(dateUpperFilter)}
                    onChange={setDateUpperFilter}
                    slotProps={{
                      textField: {
                        size: 'medium',
                        sx: { bgcolor: 'background.paper' },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>
        </Card>
      ) : (
        <Card
          className="p-6 mb-6"
          sx={{ bgcolor: 'background.paper' }}
          role="status"
          aria-busy="true"
          aria-label="Loading filters"
        >
          <Box
            className="grid grid-cols-1 gap-6 lg:grid-rows-2 lg:grid-cols-6"
            aria-hidden="true"
          >
            <Skeleton
              className="col-span-1 lg:col-span-2"
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              className="col-span-1 lg:col-span-2"
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              className="col-span-1 lg:col-span-2"
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              className="lg:col-span-3"
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
            <Skeleton
              className="lg:col-span-3"
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1 }}
            />
          </Box>
        </Card>
      )}
      <Card className="p-6 mb-6">
        <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <Box className="flex items-center gap-2">
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() =>
                navigate(ASSIGNMENTS_URLS.CREATE, {
                  state: { linkedServiceID },
                })
              }
              aria-label="Create new assignment"
              className="mb-2 md:mb-0"
              disabled={!linkedServiceID}
            >
              Create Assignment
            </Button>
            <Tooltip title="View audit logs of assignments completed by manager intervention">
              <IconButton
                aria-label="view audit logs"
                onClick={() => setAuditLogOpen(true)}
                size="medium"
                sx={{
                  minWidth: MIN_BUTTON_SIZE,
                  minHeight: MIN_BUTTON_SIZE,
                }}
              >
                <AuditLogIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            className="flex items-center justify-end gap-2"
            role="status"
            aria-live="polite"
            aria-label="Assignment statistics"
          >
            <Chip
              size="medium"
              label={`${totalAssignments} assignment${totalAssignments === 1 ? '' : 's'}`}
              color="primary"
              aria-label={`Total: ${totalAssignments} assignments`}
            />
            {isFiltered && (
              <>
                <Chip
                  size="medium"
                  label={`${filteredAssignments.length} of ${totalAssignments} shown`}
                  color="secondary"
                  variant="outlined"
                  aria-label={`Showing ${filteredAssignments.length} of ${totalAssignments} assignments after filtering`}
                />
                <Button
                  size="medium"
                  variant="outlined"
                  color="secondary"
                  onClick={resetFilters}
                  className="ml-2 whitespace-nowrap"
                  sx={{ height: 32 }}
                >
                  Reset Filters
                </Button>
              </>
            )}
          </Box>
        </Box>

        {isLoading && !isLoaded ? (
          <Skeleton
            variant="rectangular"
            height={400}
            role="status"
            aria-live="polite"
            aria-label="Loading assignments"
          />
        ) : gridRows.length === 0 ? (
          <Box className="py-16 text-center" role="status">
            <Typography variant="body1" color="text.secondary">
              No assignments found
            </Typography>
          </Box>
        ) : (
          <DataGridPremium
            rows={gridRows}
            columns={gridColumns}
            slots={{ toolbar: AssignmentsToolbar }}
            showToolbar
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: DATAGRID_DEFAULT_PAGE_SIZE,
                },
              },
              sorting: {
                sortModel: [{ field: 'dueDate', sort: 'desc' }],
              },
              density: 'standard',
              pinnedColumns: { right: ['actions'] },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            pagination
            disableRowSelectionOnClick
            sx={dataGridSx}
          />
        )}
      </Card>
      <DeleteConfirmationModal
        isOpen={!!assignmentToDelete}
        onClose={() => setAssignmentToDelete(null)}
        onConfirm={handleDeleteAssignment}
        entityName="assignment"
        entityDisplayName={assignmentToDelete?.name || ''}
        isSubmitting={isDeleting}
      />
      <AuditLogModal open={auditLogOpen} setOpen={setAuditLogOpen} />
      {NotificationComponent}
    </Container>
  )
}
